const enabled =
  import.meta.env.VITE_APP_ENV === 'local' &&
  import.meta.env.VITE_ENABLE_DEVTOOLS === 'true'

const LOG_ENDPOINT = '/__api-log'

const REDACTED = '[REDACTED]'
const SENSITIVE_HEADER_KEYS = ['authorization', 'cookie', 'set-cookie']

/**
 * Keys whose VALUES must never be recorded.
 *
 * Headers were redacted from the start but bodies were not, so a tester who hit
 * a bug anywhere near sign-in filed a report with their own password in it, in
 * plaintext, in `bug_reports.api_logs` — `/auth/login` sends `{email, password}`
 * and the whole body was stored verbatim.
 *
 * Matching is on the key with case and separators stripped, so `password`,
 * `Password`, `password_confirmation` and `newPassword` are all caught.
 * Substrings are used only for words distinctive enough to be safe there —
 * `pin` as a substring would swallow `shipping`, so short words match whole.
 */
const SENSITIVE_KEY_PARTS = [
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'credential',
  'privatekey',
]
const SENSITIVE_KEY_EXACT = [
  'pin',
  'otp',
  'cvv',
  'auth',
  'authorization',
  'mfacode',
  'twofactorcode',
  'securitycode',
]

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase().replace(/[^a-z0-9]/g, '')
  return (
    SENSITIVE_KEY_EXACT.includes(k) ||
    SENSITIVE_KEY_PARTS.some((part) => k.includes(part))
  )
}

/**
 * Copy a request/response body with every sensitive value replaced.
 *
 * Pure — the response body is handed to the app as well as the logger, so
 * redacting in place would blank fields the app actually needs. Depth is capped
 * and visited objects are tracked so a cyclic payload cannot hang the caller.
 */
export function redactSensitive(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (value === null || typeof value !== 'object') return value
  if (depth > 6) return '[TRUNCATED]'
  if (seen.has(value as object)) return '[CIRCULAR]'
  seen.add(value as object)

  if (Array.isArray(value)) return value.map((v) => redactSensitive(v, depth + 1, seen))

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? REDACTED : redactSensitive(v, depth + 1, seen)
  }
  return out
}

export interface ApiLogPayload {
  method: string
  url: string
  endpoint?: string
  status?: number
  durationMs?: number
  error?: string
  timestamp: string
  requestHeaders?: Record<string, string>
  responseHeaders?: Record<string, string>
  requestBody?: unknown
  responseBody?: unknown
}

export function isApiLoggingEnabled(): boolean {
  return enabled
}

export function headersToObject(
  headers: HeadersInit | Headers | undefined | null
): Record<string, string> | undefined {
  if (!headers) return undefined
  const out: Record<string, string> = {}
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      out[key] = value
    })
  } else if (Array.isArray(headers)) {
    for (const [k, v] of headers) out[k] = v
  } else {
    Object.assign(out, headers as Record<string, string>)
  }
  for (const k of Object.keys(out)) {
    if (SENSITIVE_HEADER_KEYS.includes(k.toLowerCase())) {
      out[k] = REDACTED
    }
  }
  return out
}

export function formDataToObject(form: FormData): Record<string, unknown> {
  const obj: Record<string, unknown> = {}
  form.forEach((value, key) => {
    const v =
      value instanceof File
        ? `[File: ${value.name || '(unnamed)'}, ${value.size}B, ${value.type || 'unknown'}]`
        : value
    if (key in obj) {
      const existing = obj[key]
      if (Array.isArray(existing)) existing.push(v)
      else obj[key] = [existing, v]
    } else {
      obj[key] = v
    }
  })
  return obj
}

export function logApiCall(payload: ApiLogPayload): void {
  if (!enabled) return
  try {
    void fetch(LOG_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // swallow — logger must never break the app
  }
}
